import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Mic, Volume2, MessageSquare, GraduationCap, Headphones, Edit3, Target, Briefcase, 
  Globe, Plane, Rocket, Users, Snowflake, ZapOff, HelpCircle, Clock, XCircle, 
  Newspaper, Tv, Laptop, MicOff, LineChart, Palette, Wrench, PieChart, HeartPulse, 
  Search, User, BookOpen, Smartphone, Film, Flame, EyeOff, AlertCircle, Smile, 
  Handshake, Coffee, FileText, Hammer, Music, Utensils, Trophy, Gamepad2, ChefHat, 
  Cat, Dumbbell, Zap, Star, Wind, Ruler, Flower2, Leaf, Sun, MonitorPlay, Youtube, 
  Facebook, Instagram, Linkedin, Twitter, MoreHorizontal, X, Check, Lock, 
  Mail, CreditCard, MapPin, Shield, TreePine, Sparkles, Info, Presentation, Network, 
  VolumeX, CheckCircle2 // <-- Added right here at the end
} from 'lucide-react';
import './OnboardingPage.css';

const OnboardingPage = () => {
  const navigate = useNavigate();
  
  // Flow State
  const [step, setStep] = useState(1);
  const totalSteps = 57; 

  // Interactive States
  const [selectedPlan, setSelectedPlan] = useState('12-week');
  const [isSpinning, setIsSpinning] = useState(false);
  const [wheelDeg, setWheelDeg] = useState(0);
  
  // User Data State
  const [goals, setGoals] = useState([]);
  const [motivation, setMotivation] = useState(''); 
  const [obstacles, setObstacles] = useState([]);
  const [dailyUse, setDailyUse] = useState([]);
  const [field, setField] = useState('');
  const [age, setAge] = useState('');
  const [gender, setGender] = useState('');
  const [currentMethods, setCurrentMethods] = useState([]);
  const [challenges, setChallenges] = useState([]);
  const [feeling, setFeeling] = useState('');
  const [hardestPart, setHardestPart] = useState([]);
  const [currentLevel, setCurrentLevel] = useState('');
  const [nativeLanguage, setNativeLanguage] = useState('');
  const [knownWords, setKnownWords] = useState([]);
  const [userName, setUserName] = useState('');
  const [workScenarios, setWorkScenarios] = useState([]);
  const [lifeOccasions, setLifeOccasions] = useState([]);
  const [topics, setTopics] = useState([]);
  const [englishStyle, setEnglishStyle] = useState([]);
  const [tutorStyle, setTutorStyle] = useState([]);
  const [userEmail, setUserEmail] = useState('');
  const [wantsUpdates, setWantsUpdates] = useState(true);
  const [tutorGender, setTutorGender] = useState('');
  const [tutorVoice, setTutorVoice] = useState('');
  const [tutorSpeed, setTutorSpeed] = useState('');
  const [correctionStyle, setCorrectionStyle] = useState('');
  const [acquisitionChannel, setAcquisitionChannel] = useState('');

  // Auto-advance for Loading Screens
  useEffect(() => {
    if (step === 31 || step === 45 || step === 48 || step === 50) { 
      const timer = setTimeout(() => {
        setStep(prev => prev + 1);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [step]);

  const nextStep = () => {
    if (step === totalSteps) {
      navigate('/dashboard'); 
    } else {
      setStep((prev) => Math.min(prev + 1, totalSteps));
    }
  };
  
  const prevStep = () => {
    if (step === 1) {
      navigate('/');
    } else {
      setStep((prev) => Math.max(prev - 1, 1));
    }
  };

  const toggleArrayItem = (setter, item) => {
    setter((prev) =>
      prev.includes(item) ? prev.filter((i) => i !== item) : [...prev, item]
    );
  };

  const handleSingleSelect = (setter, value) => {
    setter(value);
    setTimeout(() => nextStep(), 250); 
  };

  const handleSelectWithoutAdvance = (setter, value) => {
    setter(value);
  };

  const handleStartCall = async (nextStepId) => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach(track => track.stop());
      setStep(nextStepId);
    } catch (err) {
      console.error("Microphone access denied", err);
      alert("We need microphone access for the speaking test to work properly!");
    }
  };

  const canContinue = () => {
    switch (step) {
      case 1: return goals.length > 0;
      case 4: return obstacles.length > 0;
      case 6: return dailyUse.length > 0;
      case 11: return currentMethods.length > 0;
      case 14: return challenges.length > 0;
      case 17: return hardestPart.length > 0;
      case 19: return currentLevel !== '';
      case 21: return nativeLanguage !== '';
      case 30: return userName.trim() !== '';
      case 36: return workScenarios.length > 0;
      case 37: return lifeOccasions.length > 0;
      case 38: return topics.length > 0;
      case 39: return englishStyle.length > 0;
      case 40: return tutorStyle.length > 0;
      case 41: return tutorGender !== '';
      case 42: return tutorVoice !== '';
      case 43: return tutorSpeed !== '';
      case 44: return correctionStyle !== '';
      case 47: return acquisitionChannel !== '';
      case 49: return userEmail.includes('@') && userEmail.includes('.');
      case 56: return selectedPlan !== '';
      default: return true; 
    }
  };

  // ==========================================
  // STEP RENDERERS 
  // ==========================================

  const renderStep1 = () => (
    <div className="onboarding-step">
      <div className="chat-bubble-row">
        <img src="https://i.pravatar.cc/150?img=47" alt="AI Tutor" className="tutor-avatar-small" />
        <div className="chat-bubble">Which aspect of your English do you want to improve most?</div>
      </div>
      <div className="goals-grid">
        {[
          { id: 'confidence', icon: <Mic />, label: 'Speaking with confidence' },
          { id: 'pronunciation', icon: <Volume2 />, label: 'Improving pronunciation' },
          { id: 'vocabulary', icon: <MessageSquare />, label: 'Expanding vocabulary' },
          { id: 'grammar', icon: <GraduationCap />, label: 'Using grammar correctly' },
          { id: 'listening', icon: <Headphones />, label: 'Understanding native speakers' },
          { id: 'writing', icon: <Edit3 />, label: 'Writing more fluently' },
        ].map((item) => (
          <div key={item.id} className={`goal-card ${goals.includes(item.id) ? 'active' : ''}`} onClick={() => toggleArrayItem(setGoals, item.id)}>
            <div className="goal-icon">{item.icon}</div><div className="goal-label">{item.label}</div><div className="checkbox-indicator"></div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="onboarding-step">
      <h2 className="social-proof-title">500 000+ users<br/><span className="highlight-bg">speak with confidence</span><br/>using VocaAI</h2>
      <div className="rating-row"><span className="huge-percent">80%</span><span className="rating-text">RATE THEIR EXPERIENCE<br/>AS "AMAZING"</span></div>
      <div className="review-box"><p>"Better than a personal teacher because the conversations are limitless about your personal interests..."</p><div className="review-author"><span>Javier M • Jun 29, 2026</span><span className="stars">★★★★★</span></div></div>
      <div className="review-box"><p>"I've not even expected that this app will be so cool. Teacher is realistic it's interesting to make quizzes..."</p><div className="review-author"><span>An_zh_e_la • Apr 28, 2026</span><span className="stars">★★★★★</span></div></div>
    </div>
  );

  const renderStep3 = () => (
    <div className="onboarding-step">
      <div className="chat-bubble-row">
        <img src="https://i.pravatar.cc/150?img=47" alt="AI Tutor" className="tutor-avatar-small" />
        <div className="chat-bubble">Why do you want to improve your English?</div>
      </div>
      <div className="list-options">
        {[
          { id: 'work', icon: <Target />, label: 'Speak confidently at work' },
          { id: 'job', icon: <Briefcase />, label: 'Find a new job' },
          { id: 'abroad', icon: <Globe />, label: 'Live comfortably abroad' },
          { id: 'travel', icon: <Plane />, label: 'Travel with ease' },
          { id: 'skills', icon: <Rocket />, label: 'Expand my skills' },
          { id: 'family', icon: <Users />, label: 'Connect with family & friends' },
        ].map((item) => (
          <div key={item.id} className={`list-option-card single-select ${motivation === item.label ? 'active' : ''}`} onClick={() => handleSingleSelect(setMotivation, item.label)}>
            <div className="list-option-left"><span className="list-icon">{item.icon}</span><span className="list-label">{item.label}</span></div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderStep4 = () => (
    <div className="onboarding-step">
      <div className="chat-bubble-row">
        <img src="https://i.pravatar.cc/150?img=47" alt="AI Tutor" className="tutor-avatar-small" />
        <div className="chat-bubble">What's stopping you when you speak English?<br/><span className="bubble-subtext">Select all that apply.</span></div>
      </div>
      <div className="list-options">
        {[
          { id: 'freeze', icon: <Snowflake />, label: 'I freeze when I speak' },
          { id: 'express', icon: <ZapOff />, label: "I can't express myself" },
          { id: 'accent', icon: <VolumeX />, label: "My accent isn't clear" },
          { id: 'grammar', icon: <HelpCircle />, label: 'I make grammar mistakes' },
          { id: 'reply', icon: <Clock />, label: "I can't reply quickly" },
          { id: 'none', icon: <CheckCircle2 />, label: 'None of the above' },
        ].map((item) => (
          <div key={item.id} className={`list-option-card ${obstacles.includes(item.id) ? 'active' : ''}`} onClick={() => toggleArrayItem(setObstacles, item.id)}>
            <div className="list-option-left"><span className="list-icon">{item.icon}</span><span className="list-label">{item.label}</span></div><div className="checkbox-indicator"></div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderStep5 = () => (
    <div className="onboarding-step">
      <div className="chat-bubble-row"><img src="https://i.pravatar.cc/150?img=47" alt="AI Tutor" className="tutor-avatar-small" /><div className="chat-bubble">Do you agree with the statement below?</div></div>
      <div className="statement-card">I know exactly what to say in my head, but can't find the right words.</div>
      <div className="agreement-buttons">
        <button className="agree-btn no" onClick={nextStep}><span className="agree-icon"><X size={32} /></span> No</button>
        <button className="agree-btn maybe" onClick={nextStep}><span className="agree-icon"><HelpCircle size={32} /></span> Maybe</button>
        <button className="agree-btn yes" onClick={nextStep}><span className="agree-icon"><Check size={32} /></span> Yes</button>
      </div>
    </div>
  );

  const renderStep6 = () => (
    <div className="onboarding-step">
      <div className="chat-bubble-row"><img src="https://i.pravatar.cc/150?img=47" alt="AI Tutor" className="tutor-avatar-small" /><div className="chat-bubble">How do you use English in your daily life?<br/><span className="bubble-subtext">Select all that apply.</span></div></div>
      <div className="list-options">
        {[
          { id: 'news', icon: <Newspaper />, label: 'Read English news' }, { id: 'shows', icon: <Tv />, label: 'Watch English shows' }, { id: 'music', icon: <Headphones />, label: 'Listen to English music' }, { id: 'work', icon: <Laptop />, label: 'Use English at work' }, { id: 'chat', icon: <MessageSquare />, label: 'Chat with English speakers' }, { id: 'rarely', icon: <MicOff />, label: 'Rarely use English' },
        ].map((item) => (
          <div key={item.id} className={`list-option-card ${dailyUse.includes(item.id) ? 'active' : ''}`} onClick={() => toggleArrayItem(setDailyUse, item.id)}>
            <div className="list-option-left"><span className="list-icon">{item.icon}</span><span className="list-label">{item.label}</span></div><div className="checkbox-indicator"></div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderStep7 = () => (
    <div className="onboarding-step">
      <div className="chat-bubble-row"><img src="https://i.pravatar.cc/150?img=47" alt="AI Tutor" className="tutor-avatar-small" /><div className="chat-bubble">What field do you work in?</div></div>
      <div className="list-options">
        {[
          { id: 'tech', icon: <Laptop />, label: 'Tech & Engineering' }, { id: 'business', icon: <LineChart />, label: 'Business & Finance' }, { id: 'education', icon: <GraduationCap />, label: 'Students & Education' }, { id: 'creative', icon: <Palette />, label: 'Creative & Media' }, { id: 'services', icon: <Wrench />, label: 'Services & Skilled Jobs' }, { id: 'marketing', icon: <PieChart />, label: 'Marketing & Sales' }, { id: 'healthcare', icon: <HeartPulse />, label: 'Healthcare & Science' }, { id: 'unemployed', icon: <Search />, label: 'Currently unemployed' }, { id: 'other', icon: <Globe />, label: 'Other Fields' },
        ].map((item) => (
          <div key={item.id} className={`list-option-card single-select ${field === item.label ? 'active' : ''}`} onClick={() => handleSingleSelect(setField, item.label)}>
            <div className="list-option-left"><span className="list-icon">{item.icon}</span><span className="list-label">{item.label}</span></div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderStep8 = () => (
    <div className="onboarding-step interstitial-step text-center centered-content">
      {/* Money Confetti Background */}
      <div className="money-confetti-container">
        {[...Array(12)].map((_, i) => (
          <div key={i} className={`money-piece piece-${i}`}>💵</div>
        ))}
      </div>

      <div className="interstitial-content z-10 w-100 mt-4">
        <h2 className="interstitial-title" style={{ lineHeight: '1.3', fontSize: '2.4rem' }}>
          <span className="text-purple" style={{ color: '#A855F7' }}>
            {field || 'Services & Skilled Jobs'} professionals
          </span>
          <br />
          with fluent English earn <span className="text-green" style={{ color: '#22C55E' }}>up to 19% more</span> on average
        </h2>
        
        <div className="interstitial-source mt-4 mx-auto" style={{ background: 'rgba(255,255,255,0.05)', padding: '1rem 1.5rem', borderRadius: '16px', display: 'inline-flex', maxWidth: '80%' }}>
          <span className="doc-icon"><FileText size={16} /></span>
          <span style={{ fontSize: '0.9rem', color: '#9BA3C9', textAlign: 'left', marginLeft: '0.8rem' }}>
            Based on Preply Career Study 2025 & Babbel Survey
          </span>
        </div>
      </div>

    </div>
  );

  const renderStep9 = () => (
    <div className="onboarding-step">
      <div className="chat-bubble-row"><img src="https://i.pravatar.cc/150?img=47" alt="AI Tutor" className="tutor-avatar-small" /><div className="chat-bubble">What is your age?<br/><span className="bubble-subtext">Each age group learns differently. We'll adapt your practice to work best for you.</span></div></div>
      <div className="grid-options age-grid">
        {['<20', '20s', '30s', '40s', '50s', '60+'].map((a) => (<div key={a} className={`grid-card ${age === a ? 'active' : ''}`} onClick={() => handleSingleSelect(setAge, a)}>{a}</div>))}
      </div>
    </div>
  );

  const renderStep10 = () => (
    <div className="onboarding-step">
      <div className="chat-bubble-row"><img src="https://i.pravatar.cc/150?img=47" alt="AI Tutor" className="tutor-avatar-small" /><div className="chat-bubble">What is your gender?</div></div>
      <div className="grid-options gender-grid">
        {[ { id: 'male', icon: <User />, label: 'Male' }, { id: 'female', icon: <User />, label: 'Female' } ].map((g) => (<div key={g.id} className={`grid-card ${gender === g.label ? 'active' : ''}`} onClick={() => handleSingleSelect(setGender, g.label)}><div className="grid-icon">{g.icon}</div><div className="grid-label">{g.label}</div></div>))}
      </div>
    </div>
  );

  const renderStep11 = () => (
    <div className="onboarding-step">
      <div className="chat-bubble-row"><img src="https://i.pravatar.cc/150?img=47" alt="AI Tutor" className="tutor-avatar-small" /><div className="chat-bubble">How are you currently improving your English?</div></div>
      <div className="list-options">
        {[
          { id: 'classes', icon: <BookOpen />, label: 'Classes or courses' }, { id: 'apps', icon: <Smartphone />, label: 'Learning apps' }, { id: 'videos', icon: <Film />, label: 'Videos or movies' }, { id: 'podcasts', icon: <Headphones />, label: 'Podcasts or audiobooks' }, { id: 'speaking', icon: <Users />, label: 'Speaking with others' }, { id: 'none', icon: <XCircle />, label: 'Not learning right now' },
        ].map((item) => (
          <div key={item.id} className={`list-option-card ${currentMethods.includes(item.id) ? 'active' : ''}`} onClick={() => toggleArrayItem(setCurrentMethods, item.id)}>
            <div className="list-option-left"><span className="list-icon">{item.icon}</span><span className="list-label">{item.label}</span></div><div className="checkbox-indicator"></div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderStep12 = () => (
    <div className="onboarding-step interstitial-step text-center centered-content">
      <h2 className="interstitial-title mt-4">You'll remember<br/><span className="text-huge">1.5x more</span></h2>
      <p className="interstitial-subtitle mb-4">Active recall leads to better 1-week retention than passive studying</p>
      
      <div className="data-card mb-2">
        <p className="card-heading">Remember after 1 week</p>
        <div className="bar-chart">
          <div className="bar-col">
            <span className="bar-val">40%</span>
            <div className="bar-fill gray" style={{ height: '45%' }}></div>
            <span className="bar-label">Passive studying</span>
          </div>
          <div className="bar-col">
            <span className="bar-val">61%</span>
            <div className="bar-fill gradient" style={{ height: '75%' }}></div>
            <span className="bar-label">VocaAI</span>
          </div>
        </div>
        <div className="card-footer-note">
    <img src="/logo.png" alt="VocaAI Logo"  className="custom-footer-logo" />
  <span>VocaAI conversations train active recall in real time.</span>
</div>
      </div>
      
      {/* ADDED: Source text */}
      <div className="source-text text-left w-100 pl-2">
        SOURCE: ROEDIGER & KARPICKE (2006),<br/>PSYCHOLOGICAL SCIENCE
      </div>
    </div>
  );

  const renderStep13 = () => (
    <div className="onboarding-step interstitial-step text-center centered-content">
      <h2 className="interstitial-title mt-4">Why VocaAI works</h2>
      <p className="interstitial-subtitle mb-4">Research shows strong long-term gains from interactive feedback</p>
      
      <div className="data-card cycle-card mb-2">
        <div className="cycle-diagram-modern">
          {/* Subtle circular track in the background */}
          <svg className="cycle-tracks" viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg">
            <circle cx="100" cy="100" r="85" fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="1" strokeDasharray="4 4" />
          </svg>

          {/* The 3 floating nodes */}
          <div className="cycle-node-modern top-left">
            <Headphones size={16} /> <span>You hear</span>
          </div>
          <div className="cycle-node-modern top-right">
            <Mic size={16} /> <span>You speak</span>
          </div>
          <div className="cycle-node-modern bottom-center">
            <MessageSquare size={16} /> <span>You get feedback</span>
          </div>

          {/* Center Logo & Text */}
          <div className="cycle-center-modern">
            <div className="center-logo">
              {/* Replace src with your actual logo path */}
              <img src="/logo.png" alt="VocaAI Logo" className="center-logo-img" />
            </div>
            <span className="center-text">Every VocaAI<br/>conversation</span>
          </div>
        </div>

        {/* Source Footer */}
        <div className="card-divider"></div>
        <div className="source-text text-left mt-3">
          SOURCES: KRASHEN (1985), SWAIN (1985), MACKEY & GOO (2007)
        </div>
      </div>
    </div>
  );

  const renderStep14 = () => (
    <div className="onboarding-step">
      <div className="chat-bubble-row"><img src="https://i.pravatar.cc/150?img=47" alt="AI Tutor" className="tutor-avatar-small" /><div className="chat-bubble">What are the main challenges for you in learning English?</div></div>
      <div className="list-options">
        {[
          { id: 'time', label: 'Finding time' }, { id: 'motivation', label: 'Staying motivated' }, { id: 'chances', label: 'Few chances to speak' }, { id: 'remembering', label: 'Remembering lessons' }, { id: 'nervous', label: 'Feeling nervous speaking' }, { id: 'hard', label: 'English feels too hard' }, { id: 'none', label: "No struggle — I'm doing great!" },
        ].map((item) => (
          <div key={item.id} className={`list-option-card plain-text ${challenges.includes(item.id) ? 'active' : ''}`} onClick={() => toggleArrayItem(setChallenges, item.id)}>
            <div className="list-option-left"><span className="list-label">{item.label}</span></div><div className="checkbox-indicator"></div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderStep15 = () => (
    <div className="onboarding-step">
      <div className="chat-bubble-row"><img src="https://i.pravatar.cc/150?img=47" alt="AI Tutor" className="tutor-avatar-small" /><div className="chat-bubble">How do you feel when you need to speak English?</div></div>
      <div className="feelings-grid">
        {[
          { id: 'anxious', icon: <Flame />, label: 'I get anxious and start sweating' }, { id: 'freeze', icon: <Snowflake />, label: 'I freeze and forget words' }, { id: 'avoid', icon: <EyeOff />, label: 'I avoid speaking if possible' }, { id: 'embarrassed', icon: <AlertCircle />, label: 'I speak, but feel embarrassed' }, { id: 'confident', icon: <Smile />, label: 'I feel confident enough' },
        ].map((f) => (
          <div key={f.id} className={`feeling-card ${feeling === f.id ? 'active' : ''}`} onClick={() => handleSingleSelect(setFeeling, f.id)}>
            <div className="feeling-icon">{f.icon}</div><div className="feeling-label">{f.label}</div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderStep16 = () => (
    <div className="onboarding-step">
      <div className="chat-bubble-row"><img src="https://i.pravatar.cc/150?img=47" alt="AI Tutor" className="tutor-avatar-small" /><div className="chat-bubble">Do you agree with the statement below?</div></div>
      <div className="statement-card">I feel uncomfortable speaking English because I don't practice enough.</div>
      <div className="agreement-buttons">
        <button className="agree-btn no" onClick={nextStep}><span className="agree-icon"><X size={32} /></span> No</button>
        <button className="agree-btn maybe" onClick={nextStep}><span className="agree-icon"><HelpCircle size={32} /></span> Maybe</button>
        <button className="agree-btn yes" onClick={nextStep}><span className="agree-icon"><Check size={32} /></span> Yes</button>
      </div>
    </div>
  );

  const renderStep17 = () => (
    <div className="onboarding-step">
      <div className="chat-bubble-row"><img src="https://i.pravatar.cc/150?img=47" alt="AI Tutor" className="tutor-avatar-small" /><div className="chat-bubble">What's the hardest part of speaking English for you?</div></div>
      <div className="list-options">
        {[
          { id: 'starting', label: 'Starting conversations' }, { id: 'flow', label: 'Maintaining a natural flow' }, { id: 'words', label: 'Finding the right words quickly' }, { id: 'accents', label: 'Understanding different accents' }, { id: 'complex', label: 'Expressing complex thoughts' }
        ].map((item) => (
          <div key={item.id} className={`list-option-card plain-text ${hardestPart.includes(item.id) ? 'active' : ''}`} onClick={() => toggleArrayItem(setHardestPart, item.id)}>
            <div className="list-option-left"><span className="list-label">{item.label}</span></div><div className="checkbox-indicator"></div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderStep18 = () => (
    <div className="onboarding-step interstitial-step text-center">
      <h2 className="interstitial-title">You're doing just fine 🫶</h2>
      <div className="emoji-grid">
        <span><User /></span><span><User /></span><span><User /></span><span><User /></span><span><User /></span>
        <span><User /></span><span><User /></span><span><AlertCircle /></span><span><User /></span><span><User /></span>
      </div>
      <p className="interstitial-subtitle mt-2">7 out of 10 also struggle with spontaneous speaking, even after years of study</p>
    </div>
  );

  const renderStep19 = () => (
    <div className="onboarding-step">
      <div className="chat-bubble-row"><img src="https://i.pravatar.cc/150?img=47" alt="AI Tutor" className="tutor-avatar-small" /><div className="chat-bubble">What's your current language level?</div></div>
      <div className="list-options">
        {[
          { id: 'a1', title: 'Beginner', badge: 'A1 - A2', desc: 'I can buy things & order food' }, { id: 'b1', title: 'Intermediate', badge: 'B1', desc: 'I can have simple conversations about everyday topics' }, { id: 'b2', title: 'Upper-Intermediate', badge: 'B2', desc: 'I can participate in discussions and explain my opinions' }, { id: 'c1', title: 'Advanced', badge: 'C1 - C2', desc: 'I speak confidently in any situation, including work and social settings' }
        ].map((item) => (
          <div key={item.id} className={`list-option-card level-card ${currentLevel === item.id ? 'active' : ''}`} onClick={() => handleSelectWithoutAdvance(setCurrentLevel, item.id)}>
            <div className="level-header"><span className="level-title">{item.title}</span><span className="level-badge">{item.badge}</span></div><p className="level-desc">{item.desc}</p>
          </div>
        ))}
      </div>
    </div>
  );

  const renderStep20 = () => (
    <div className="onboarding-step interstitial-step text-center centered-content">
      {/* Background glow wrapper */}
      <div className="offer-glow-wrapper my-4">
        <div className="offer-card premium-offer text-center">
          
          <div className="limited-tag flex-center gap-1">
            <Zap size={14} fill="#FFD700" color="#FFD700" /> LIMITED TIME
          </div>

          <div className="offer-icon-circle mx-auto mb-3">
            <Target size={32} color="var(--brand-orange)" />
          </div>

          <h3 className="mb-1">English Assessment</h3>
          <p className="offer-subtitle mb-3">Comprehensive fluency analysis</p>
          
          <div className="price-tag justify-center mt-2">
            <span className="strike text-muted font-bold">$29.99</span>
            <span className="free-badge pulse-badge">FREE</span>
          </div>
          
        </div>
      </div>

      <h2 className="interstitial-title small-margin">
        Many of us underestimate our English level. <br/>
        <span className="text-purple">That's normal.</span>
      </h2>
      <p className="interstitial-subtitle mt-2">
        Our AI English Tutor uses advanced speech recognition to analyze your actual skills in real-time.
      </p>
    </div>
  );
const renderStep21 = () => (
    <div className="onboarding-step">
      <div className="chat-bubble-row">
        <img src="https://i.pravatar.cc/150?img=47" alt="AI Tutor" className="tutor-avatar-small" />
        <div className="chat-bubble">What is your native language?</div>
      </div>
      <div className="list-options">
        {[
          { id: 'es', icon: '🇪🇸', label: 'Spanish', sub: 'español' },
          { id: 'pt', icon: '🇧🇷', label: 'Portuguese', sub: 'português' },
          { id: 'hi', icon: '🇮🇳', label: 'Hindi', sub: 'हिन्दी' },
          { id: 'ru', icon: '🇷🇺', label: 'Russian', sub: 'русский' },
          { id: 'cn', icon: '🇨🇳', label: 'Chinese', sub: '中文' },
          { id: 'ar', icon: '🇸🇦', label: 'Arabic', sub: 'العربية' },
          { id: 'fr', icon: '🇫🇷', label: 'French', sub: 'français' },
          { id: 'tr', icon: '🇹🇷', label: 'Turkish', sub: 'Türkçe' },
          { id: 'it', icon: '🇮🇹', label: 'Italian', sub: 'italiano' },
          { id: 'de', icon: '🇩🇪', label: 'German', sub: 'Deutsch' },
          { id: 'kr', icon: '🇰🇷', label: 'Korean', sub: '한국어' },
          { id: 'jp', icon: '🇯🇵', label: 'Japanese', sub: '日本語' },
          { id: 'he', icon: '🇮🇱', label: 'Hebrew', sub: 'עברית' },
          { id: 'pl', icon: '🇵🇱', label: 'Polish', sub: 'polski' },
          { id: 'ua', icon: '🇺🇦', label: 'Ukrainian', sub: 'українська' },
          { id: 'id', icon: '🇮🇩', label: 'Indonesian', sub: 'Indonesia' },
          { id: 'vn', icon: '🇻🇳', label: 'Vietnamese', sub: 'Tiếng Việt' },
          { id: 'th', icon: '🇹🇭', label: 'Thai', sub: 'ไทย' },
          { id: 'en', icon: '🇬🇧', label: 'English', sub: null },
          { id: 'other', icon: '🌍', label: 'My language is not listed', sub: null }
        ].map((item) => (
          <div key={item.id} className={`list-option-card lang-card ${nativeLanguage === item.label ? 'active' : ''}`} onClick={() => handleSelectWithoutAdvance(setNativeLanguage, item.label)}>
            <div className="list-option-left">
              <span className="lang-icon" style={{ fontSize: '1.1rem' }}>{item.icon}</span>
              <span className="list-label">
                {item.label} {item.sub && <span className="lang-sub">• {item.sub}</span>}
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderStep22 = () => (
    <div className="onboarding-step text-center centered-content">
      <h2 className="interstitial-title mt-2">Let's analyze your English</h2>
      <p className="interstitial-subtitle mb-4">Make a quick call with our AI tutor to get your English level and a personal improvement plan.</p>
      
      <div className="radar-mockup-card">
        {/* Progress Pill Bar */}
        <div className="radar-level-bar">
          <div className="radar-level-fill"></div>
          <div className="radar-level-labels">
            <span>A1</span><span>A2</span><span>B1</span><span style={{fontWeight: 800}}>B2</span><span className="dim">C1</span><span className="dim">C2</span>
          </div>
        </div>
        
        <p className="radar-level-text">Upper-Intermediate <span className="text-blue">B2</span></p>
        
        {/* Custom SVG Radar Chart */}
        <div className="radar-chart-container">
          <svg viewBox="0 0 200 200" className="radar-svg">
            {/* Background Grid Pentagons */}
            <polygon points="100,30 166.5,78 141,156 59,156 33.5,78" fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="1"/>
            <polygon points="100,55 142.7,86 126.4,136 73.6,136 57.3,86" fill="none" stroke="rgba(255,255,255,0.05)" strokeWidth="1"/>
            <polygon points="100,80 119,93 111,116 89,116 81,93" fill="none" stroke="rgba(255,255,255,0.03)" strokeWidth="1"/>
            
            {/* Spider Web Axes */}
            <line x1="100" y1="100" x2="100" y2="30" stroke="rgba(255,255,255,0.08)" strokeWidth="1"/>
            <line x1="100" y1="100" x2="166.5" y2="78" stroke="rgba(255,255,255,0.08)" strokeWidth="1"/>
            <line x1="100" y1="100" x2="141" y2="156" stroke="rgba(255,255,255,0.08)" strokeWidth="1"/>
            <line x1="100" y1="100" x2="59" y2="156" stroke="rgba(255,255,255,0.08)" strokeWidth="1"/>
            <line x1="100" y1="100" x2="33.5" y2="78" stroke="rgba(255,255,255,0.08)" strokeWidth="1"/>

            {/* Blue Filled Data Polygon */}
            <polygon points="100,40.5 143.2,85.1 128.7,144.8 68.5,127.3 49.5,83.5" fill="rgba(50, 98, 255, 0.15)" stroke="var(--brand-blue)" strokeWidth="1.5"/>
            
            {/* Data Points on the polygon edges */}
            <circle cx="100" cy="40.5" r="2.5" fill="var(--brand-blue)" />
            <circle cx="143.2" cy="85.1" r="2.5" fill="var(--text-muted)" />
            <circle cx="128.7" cy="144.8" r="2.5" fill="var(--brand-blue)" />
            <circle cx="68.5" cy="127.3" r="2.5" fill="var(--text-muted)" />
            <circle cx="49.5" cy="83.5" r="2.5" fill="var(--brand-blue)" />
          </svg>

          {/* HTML Labels Overlaid on SVG */}
          <div className="radar-label radar-top">
            <span className="val blue">85%</span><br/>Pronunciation
          </div>
          <div className="radar-label radar-top-right">
            <span className="val">65%</span><br/>Vocabulary
          </div>
          <div className="radar-label radar-bottom-right">
            <span className="val blue">70%</span><br/>Grammar
          </div>
          <div className="radar-label radar-bottom-left">
            <span className="val">45%</span><br/>Fluency
          </div>
          <div className="radar-label radar-top-left">
            <span className="val blue">75%</span><br/>Filler Words
          </div>
        </div>

        {/* Bottom Fade out effect */}
        <div className="radar-card-fade"></div>
      </div>
      
      <div className="mt-auto mb-4 flex-center gap-1">
        <Check size={16} color="#12C2A3" strokeWidth={3} /> 
        <span className="text-sm color-gray font-bold">1M+ personalized assessments done</span>
      </div>
      
      <button className="continue-blue-btn flex-center gap-2" onClick={() => setStep(23)}>
        <Mic size={20} /> Get Started
      </button>
    </div>
  );

  const renderStep23 = () => (
    <div className="onboarding-step interstitial-step text-center centered-content">
      <div className="avatar-tooltip-container">
        <img src="https://i.pravatar.cc/150?img=47" alt="AI Tutor" className="tutor-avatar-large mb-4" />
      </div>
      <h2 className="interstitial-title small-margin">Intro call with Stacy</h2>
      <p className="interstitial-subtitle mb-4">Assess your English<br/>and get to know your AI tutor</p>
      
      <div className="intro-call-card">
        <div className="intro-bullet"><span className="intro-bullet-icon"><Clock size={18} /></span> About 4 minutes</div>
        <div className="intro-bullet"><span className="intro-bullet-icon"><Shield size={18} /></span> Friendly and judgment-free</div>
        <div className="intro-bullet"><span className="intro-bullet-icon"><Mic size={18} /></span> Speak for at least <strong>30s</strong></div>
      </div>
      <div className="dual-buttons w-100">
        <button className="dark-btn flex-1" onClick={() => setStep(24)}>Skip</button>
        <button className="continue-blue-btn flex-1 flex-center gap-1" onClick={() => handleStartCall(26)}><Mic size={20} /> Start Call</button>
      </div>
    </div>
  );

  const renderStep24 = () => (
    <div className="onboarding-step interstitial-step text-center centered-content">
      <h2 className="interstitial-title">Are you sure?</h2>
      <div className="avatar-tooltip-container"><img src="https://i.pravatar.cc/150?img=47" alt="AI Tutor" className="tutor-avatar-large" /><div className="tooltip-bubble">4 min?<br/>Easy! 😎</div></div>
      <div className="dual-buttons mt-auto w-100">
        <button className="dark-btn flex-1" onClick={() => setStep(25)}>Skip</button>
        <button className="continue-blue-btn flex-1" onClick={() => setStep(23)}>Let's do it!</button>
      </div>
    </div>
  );

  const renderStep25 = () => (
    <div className="onboarding-step interstitial-step text-center centered-content">
      <h2 className="interstitial-title">You can always take<br/>the assessment later!</h2>
      <div className="avatar-tooltip-container"><img src="https://i.pravatar.cc/150?img=47" alt="AI Tutor" className="tutor-avatar-large" /><div className="tooltip-bubble">Let's talk<br/>later!</div></div>
      <button className="continue-blue-btn w-100 mt-auto" onClick={() => setStep(26)}>Continue</button>
    </div>
  );

  const renderStep26 = () => (
    <div className="onboarding-step interstitial-step text-center centered-content">
      <div className="avatar-tooltip-container mb-2"><img src="https://i.pravatar.cc/150?img=47" alt="AI Tutor" className="tutor-avatar-large" /></div>
      <h2 className="interstitial-title">Let's quickly check<br/>your vocabulary!</h2>
      <button className="continue-blue-btn w-100 mt-auto" onClick={nextStep}>Continue</button>
    </div>
  );

  const vocab1 = [ { id: 'improve', label: 'improve' }, { id: 'acquisition', label: 'acquisition' }, { id: 'opportunity', label: 'opportunity' }, { id: 'decision', label: 'decision' }, { id: 'recognize', label: 'recognize' }, { id: 'solution', label: 'solution' } ];
  const vocab2 = [ { id: 'enough', label: 'enough' }, { id: 'place', label: 'place' }, { id: 'procrastinate', label: 'procrastinate' }, { id: 'inference', label: 'inference' }, { id: 'confidence', label: 'confidence' }, { id: 'never', label: 'never' } ];
  const vocab3 = [ { id: 'stalemate', label: 'stalemate' }, { id: 'time', label: 'time' }, { id: 'imperative', label: 'imperative' }, { id: 'negotiate', label: 'negotiate' }, { id: 'subsequent', label: 'subsequent' }, { id: 'another', label: 'another' } ];

  const renderVocabStep = (words) => (
    <div className="onboarding-step">
      <h2 className="section-heading mb-4">Select all the words you know</h2>
      <div className="vocab-grid">
        {words.map((item) => (
          <div key={item.id} className={`vocab-card ${knownWords.includes(item.id) ? 'active' : ''}`} onClick={() => toggleArrayItem(setKnownWords, item.id)}>
            <span className="vocab-label">{item.label}</span><div className="checkbox-indicator small"></div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderStep27 = () => renderVocabStep(vocab1);
  const renderStep28 = () => renderVocabStep(vocab2);
  const renderStep29 = () => renderVocabStep(vocab3);

  const renderStep30 = () => (
    <div className="onboarding-step">
      <h2 className="section-heading">What should we call you?</h2>
      <div className="input-field-container mt-2"><span className="input-icon"><User /></span><input type="text" className="dark-input" placeholder="Name" value={userName} onChange={(e) => setUserName(e.target.value)} /></div>
    </div>
  );

  const renderStep31 = () => (
    <div className="onboarding-step text-center centered-content">
      <h2 className="interstitial-title mt-4">Evaluating your<br/>vocabulary skills</h2>
      <div className="loading-circle-wrapper my-4"><div className="loading-circle"><div className="inner-circle">34%</div></div></div>
    </div>
  );

  const renderStep32 = () => (
    <div className="onboarding-step text-center">
      <h4 className="text-sm color-gray mb-2">Your Vocabulary Level</h4>
      <h1 className="hero-title mb-1">Beginner A1</h1>
      <p className="interstitial-subtitle">6 out of 100</p>
      
      <div className="dual-buttons mt-4 mb-4">
        <div className="stat-box flex-1"><div className="stat-num">9</div><div className="stat-label">Active vocabulary</div></div>
        <div className="stat-box flex-1"><div className="stat-num">94%</div><div className="stat-label">Below average for your level</div></div>
      </div>

      <div className="vocab-dist-chart">
        {[ { l: 'A1', p: '28%', h: '60%' }, { l: 'A2', p: '26%', h: '55%' }, { l: 'B1', p: '18%', h: '40%' }, { l: 'B2', p: '13%', h: '30%' }, { l: 'C1', p: '10%', h: '25%' }, { l: 'C2', p: '5%', h: '15%' } ].map((b, i) => (
          <div key={i} className="dist-bar-wrapper"><span className="dist-percent">{b.p}</span><div className="dist-bar" style={{height: b.h}}></div><span className="dist-label">{b.l}</span></div>
        ))}
      </div>

      <div className="shield-note mt-auto text-left flex-center gap-1">
        <span className="shield-icon" style={{color: '#12C2A3'}}><Info size={20} /></span><span>This shows <strong>only 25% of your English skills.</strong></span>
      </div>
    </div>
  );

  const renderStep33 = () => (
    <div className="onboarding-step interstitial-step text-center centered-content bg-glow-gold">
      <div className="gold-coin mb-4"><BookOpen size={48} /><span className="coin-text">PRE INTERMEDIATE</span></div>
      <h2 className="interstitial-title">You've got an<br/>impressive base.</h2>
      <p className="interstitial-subtitle">Our AI Tutor is built for learners like you: driven, smart, and ready to speak confidently in high-stakes moments.</p>
    </div>
  );

  const renderStep34 = () => (
    <div className="onboarding-step text-center">
      <h4 className="text-sm color-gray mb-2">Your English Profile</h4>
      <h2 className="interstitial-title mb-4">1 of 4 Unlocked</h2>
      <p className="interstitial-subtitle mb-4">Speak for 4 minutes to unlock your complete profile</p>

      <div className="profile-grid">
        <div className="profile-card locked"><Lock size={24} className="mb-1" /> Pronunciation</div>
        <div className="profile-card unlocked"><span className="text-blue flex-center gap-1"><BookOpen size={20} /> Vocabulary</span><span className="text-sm color-gray mt-1">B2/A1</span></div>
        <div className="profile-card locked"><Lock size={24} className="mb-1" /> Grammar</div>
        <div className="profile-card locked"><Lock size={24} className="mb-1" /> Fluency</div>
      </div>

      <div className="dual-buttons w-100 mt-auto pb-4">
        <button className="dark-btn flex-1" onClick={() => setStep(35)}>Skip speaking</button>
        <button className="continue-blue-btn flex-1 flex-center gap-1" onClick={() => handleStartCall(35)}><Mic size={20} /> Speak now</button>
      </div>
    </div>
  );

  const renderStep35 = () => (
    <div className="onboarding-step interstitial-step text-center centered-content">
      <h2 className="interstitial-title">We use a Science-backed<br/>"Learn by Doing" method</h2>
      <p className="interstitial-subtitle mb-4">It's the most effective way to gain real speaking skills: you talk, get feedback, and grow — just like in real life.</p>
      
      <div className="text-sm color-gray font-bold mb-3">PIONEERED BY STUDIES FROM WORLD-CLASS INSTITUTIONS</div>
      <div className="university-logos flex-center gap-2">
        <div className="u-logo flex-center gap-1"><Shield size={20} /> Harvard</div>
        <div className="u-logo flex-center gap-1"><TreePine size={20} /> Stanford</div>
      </div>
    </div>
  );

  const renderStep36 = () => (
    <div className="onboarding-step">
      <div className="chat-bubble-row"><img src="https://i.pravatar.cc/150?img=47" alt="AI Tutor" className="tutor-avatar-small" /><div className="chat-bubble">Which work scenarios would you like to focus on?<br/><span className="bubble-subtext">Select all that apply.</span></div></div>
      <div className="list-options">
        {[ { id: 'w1', icon: <Briefcase />, label: 'Job interviews' }, { id: 'w2', icon: <Presentation />, label: 'Meetings and presentations' }, { id: 'w3', icon: <Handshake />, label: 'Negotiations and sales' }, { id: 'w4', icon: <Coffee />, label: 'Daily communication at work' }, { id: 'w5', icon: <Network />, label: 'Business networking' }, { id: 'w6', icon: <FileText />, label: 'Writing emails and reports' }].map((item) => (
          <div key={item.id} className={`list-option-card ${workScenarios.includes(item.id) ? 'active' : ''}`} onClick={() => toggleArrayItem(setWorkScenarios, item.id)}>
            <div className="list-option-left"><span className="list-icon">{item.icon}</span><span className="list-label">{item.label}</span></div><div className="checkbox-indicator"></div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderStep37 = () => (
    <div className="onboarding-step">
      <div className="chat-bubble-row"><img src="https://i.pravatar.cc/150?img=47" alt="AI Tutor" className="tutor-avatar-small" /><div className="chat-bubble">Are there any occasions in your life you'd like to be prepared for?</div></div>
      <div className="list-options">
        {[ { id: 'o1', icon: <Briefcase />, label: 'New job' }, { id: 'o2', icon: <Plane />, label: 'Moving to another country' }, { id: 'o3', icon: <Handshake />, label: 'Job interview' }, { id: 'o4', icon: <GraduationCap />, label: 'Exam' }, { id: 'o5', icon: <Sun />, label: 'Vacation abroad' }, { id: 'o6', icon: <XCircle />, label: 'None of the above' }].map((item) => (
          <div key={item.id} className={`list-option-card ${lifeOccasions.includes(item.id) ? 'active' : ''}`} onClick={() => toggleArrayItem(setLifeOccasions, item.id)}>
            <div className="list-option-left"><span className="list-icon">{item.icon}</span><span className="list-label">{item.label}</span></div><div className="checkbox-indicator"></div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderStep38 = () => (
    <div className="onboarding-step">
      <h2 className="section-heading mb-4 text-center">What topics interest you?</h2>
      <div className="topics-grid">
        {[
          { id: 't1', icon: <Film />, label: 'Movies' }, { id: 't2', icon: <Smile />, label: 'Humor' }, { id: 't3', icon: <Laptop />, label: 'Tech' },
          { id: 't4', icon: <Hammer />, label: 'DIY' }, { id: 't5', icon: <Music />, label: 'Music' }, { id: 't6', icon: <Briefcase />, label: 'Business' },
          { id: 't7', icon: <Newspaper />, label: 'News' }, { id: 't8', icon: <Utensils />, label: 'Food' }, { id: 't9', icon: <Trophy />, label: 'Sports' },
          { id: 't10', icon: <Gamepad2 />, label: 'Gaming' }, { id: 't11', icon: <Palette />, label: 'Art' }, { id: 't12', icon: <Plane />, label: 'Travel' },
          { id: 't13', icon: <ChefHat />, label: 'Cooking' }, { id: 't14', icon: <Cat />, label: 'Pets' }, { id: 't15', icon: <Dumbbell />, label: 'Fitness' }
        ].map((t) => (
          <div key={t.id} className={`topic-card ${topics.includes(t.id) ? 'active' : ''}`} onClick={() => toggleArrayItem(setTopics, t.id)}>
            <div className="topic-icon">{t.icon}</div><div className="topic-label">{t.label}</div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderStep39 = () => (
    <div className="onboarding-step">
      <h2 className="section-heading mb-4 text-center">What style of English would you like to practice?</h2>
      <div className="list-options">
        {[
          { id: 's1', icon: <Coffee />, label: 'Casual English', desc: 'Informal expressions, daily conversations' },
          { id: 's2', icon: <Briefcase />, label: 'Business English', desc: 'Formal communication, professional vocab' },
          { id: 's3', icon: <Zap />, label: 'Slang and idioms', desc: 'Authentic expressions by native speakers' },
          { id: 's4', icon: <GraduationCap />, label: 'Academic English', desc: 'Formal language for education & research' },
          { id: 's5', icon: <Plane />, label: 'Travel English', desc: 'Conversations while traveling' }
        ].map((item) => (
          <div key={item.id} className={`list-option-card detailed ${englishStyle.includes(item.id) ? 'active' : ''}`} onClick={() => toggleArrayItem(setEnglishStyle, item.id)}>
            <div className="list-option-left align-start">
              <span className="list-icon">{item.icon}</span>
              <div className="detailed-content"><div className="detailed-title">{item.label}</div><div className="detailed-desc">{item.desc}</div></div>
            </div>
            <div className="checkbox-indicator"></div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderStep40 = () => (
    <div className="onboarding-step">
      <div className="chat-bubble-row"><img src="https://i.pravatar.cc/150?img=47" alt="AI Tutor" className="tutor-avatar-small" /><div className="chat-bubble">Build your ideal tutor style<br/><span className="bubble-subtext">Select all that apply.</span></div></div>
      <div className="list-options">
        {[
          { id: 'ts1', icon: <Star />, label: 'Cheerful' }, { id: 'ts2', icon: <Zap />, label: 'Energetic' }, { id: 'ts3', icon: <Wind />, label: 'Patient' }, { id: 'ts4', icon: <Ruler />, label: 'Strict' }, { id: 'ts5', icon: <BookOpen />, label: 'Academic' }, { id: 'ts6', icon: <Users />, label: 'Friendly' }
        ].map((item) => (
          <div key={item.id} className={`list-option-card ${tutorStyle.includes(item.id) ? 'active' : ''}`} onClick={() => toggleArrayItem(setTutorStyle, item.id)}>
            <div className="list-option-left"><span className="list-icon">{item.icon}</span><span className="list-label">{item.label}</span></div><div className="checkbox-indicator"></div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderStep41 = () => (
    <div className="onboarding-step">
      <div className="chat-bubble-row"><img src="https://i.pravatar.cc/150?img=47" alt="AI Tutor" className="tutor-avatar-small" /><div className="chat-bubble">Do you prefer a male or female voice for your AI tutor?</div></div>
      <div className="grid-options gender-grid mt-4">
        {[ { id: 'male', icon: <User />, label: 'Male' }, { id: 'female', icon: <User />, label: 'Female' } ].map((g) => (
          <div key={g.id} className={`grid-card ${tutorGender === g.label ? 'active' : ''}`} onClick={() => handleSingleSelect(setTutorGender, g.label)}>
            <div className="grid-icon">{g.icon}</div><div className="grid-label">{g.label}</div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderStep42 = () => (
    <div className="onboarding-step">
      <h2 className="section-heading text-center mb-2">Which tutor's voice do you prefer?</h2>
      <p className="interstitial-subtitle text-center mb-4">This only affects how the voice sounds (intonation & clarity).</p>
      <div className="list-options">
        {[
          { id: 'v1', icon: <Zap />, label: 'Energetic and lively' }, { id: 'v2', icon: <Flower2 />, label: 'Soft and warm' }, 
          { id: 'v3', icon: <Leaf />, label: 'Natural and balanced' }, { id: 'v4', icon: <Sun />, label: 'Bright and clear' }
        ].map((item) => (
          <div key={item.id} className={`list-option-card ${tutorVoice === item.label ? 'active' : ''}`} onClick={() => setTutorVoice(item.label)}>
            <div className="list-option-left"><span className="list-icon">{item.icon}</span><span className="list-label">{item.label}</span></div>
            <span className="audio-icon"><Volume2 /></span>
          </div>
        ))}
      </div>
    </div>
  );

  const renderStep43 = () => (
    <div className="onboarding-step">
      <h2 className="section-heading text-center mb-2">How fast should your tutor talk?</h2>
      <p className="interstitial-subtitle text-center mb-4">Tap the icons to hear examples. You can always change it later.</p>
      <div className="list-options">
        {[
          { id: 's1', label: 'Very slow', desc: '0.7x • Clear & by syllables' }, { id: 's2', label: 'Slow', desc: '0.8x • Slightly slower than normal' }, 
          { id: 's3', label: 'Normal', desc: '1x • Natural pace' }, { id: 's4', label: 'Fast', desc: '1.2x • Fluent, native-like speech' }
        ].map((item) => (
          <div key={item.id} className={`list-option-card detailed ${tutorSpeed === item.label ? 'active' : ''}`} onClick={() => setTutorSpeed(item.label)}>
            <div className="list-option-left align-start">
              <div className="detailed-content"><div className="detailed-title">{item.label}</div><div className="detailed-desc">{item.desc}</div></div>
            </div>
            <span className="audio-icon"><Volume2 /></span>
          </div>
        ))}
      </div>
    </div>
  );

  const renderStep44 = () => (
    <div className="onboarding-step">
      <h2 className="section-heading text-center mb-4">When it comes to corrections, what suits you best?</h2>
      <div className="list-options">
        {[
          { id: 'c1', label: 'Correct me immediately' }, { id: 'c2', label: 'Let me finish speaking, then correct' }, 
          { id: 'c3', label: 'Only correct major mistakes' }, { id: 'c4', label: 'Focus on fluency, not corrections' }
        ].map((item) => (
          <div key={item.id} className={`list-option-card plain-text ${correctionStyle === item.label ? 'active' : ''}`} onClick={() => setCorrectionStyle(item.label)}>
            <div className="list-option-left"><span className="list-label">{item.label}</span></div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderStep45 = () => (
    <div className="onboarding-step text-center centered-content">
      <h2 className="interstitial-title mt-4 mb-4">Matching you<br/>with the right Tutor...</h2>
      
      <div className="multi-progress-block mt-4">
        <div className="multi-progress-item">
          <span className="multi-progress-label">Tuning to your goals</span>
          <div className="multi-progress-bar"><div className="multi-fill f1"></div></div>
        </div>
        <div className="multi-progress-item">
          <span className="multi-progress-label">Setting your tutor's vibe</span>
          <div className="multi-progress-bar"><div className="multi-fill f2"></div></div>
        </div>
        <div className="multi-progress-item">
          <span className="multi-progress-label">Adjusting voice & pace</span>
          <div className="multi-progress-bar"><div className="multi-fill f3"></div></div>
        </div>
        <div className="multi-progress-item">
          <span className="multi-progress-label">Customizing feedback style</span>
          <div className="multi-progress-bar"><div className="multi-fill f4"></div></div>
        </div>
      </div>
    </div>
  );

  const renderStep46 = () => (
    <div className="onboarding-step text-center">
      <h2 className="interstitial-title mt-2 mb-2">Meet your ideal AI tutor</h2>
      <p className="interstitial-subtitle mb-4">You can always adjust your tutor later to fit your evolving needs.</p>
      
      <div className="tutor-match-card text-left">
        <div className="match-badge">BEST MATCH</div>
        <div className="match-header flex-center align-start gap-2 mb-3">
          <img src="https://i.pravatar.cc/150?img=11" alt="Kyle" className="tutor-avatar-large" style={{width: '90px', height: '90px'}} />
          <div>
            <h3 className="mb-1 text-white">Energetic Kyle US</h3>
            <p className="text-sm color-gray" style={{lineHeight: 1.4}}>High-energy and uplifting. Challenges you and celebrates progress.</p>
          </div>
        </div>
        
        <h4 className="text-sm text-white mb-2 mt-4">Why we think Kyle fits you:</h4>
        <div className="tag-cloud">
          <span className="tutor-tag">Energetic</span><span className="tutor-tag">Loves Business</span>
          <span className="tutor-tag">Cheerful</span><span className="tutor-tag">Travel English expert</span>
        </div>
        
        <div className="shield-note mt-4 bg-transparent border-none p-0 flex-center gap-1" style={{justifyContent: 'flex-start'}}>
          <span className="shield-icon text-sm" style={{color: '#FF8A3D'}}><Sparkles size={16} /></span><span className="text-sm">Trusted by 140k+ learners like you</span>
        </div>
      </div>

      <button className="continue-blue-btn mt-auto" onClick={nextStep}>Start learning with Kyle</button>
    </div>
  );

  const renderStep47 = () => (
    <div className="onboarding-step">
      <h2 className="section-heading text-center mb-4 mt-2">How did you hear about VocaAI?</h2>
      <div className="topics-grid channel-grid">
        {[
          { id: 'ch1', icon: <MonitorPlay />, label: 'I saw ads' }, { id: 'ch2', icon: <Youtube />, label: 'YouTube' }, 
          { id: 'ch3', icon: <Smartphone />, label: 'TikTok' }, { id: 'ch4', icon: <Facebook />, label: 'Facebook reels' }, 
          { id: 'ch5', icon: <Instagram />, label: 'Instagram reels' }, { id: 'ch6', icon: <Linkedin />, label: 'LinkedIn' }, 
          { id: 'ch7', icon: <Search />, label: 'Google' }, { id: 'ch8', icon: <Users />, label: 'Friends told me' }, 
          { id: 'ch9', icon: <Twitter />, label: 'X/Twitter' }, { id: 'ch10', icon: <MoreHorizontal />, label: 'Other' }
        ].map((t) => (
          <div key={t.id} className={`topic-card ${acquisitionChannel === t.label ? 'active' : ''}`} onClick={() => handleSingleSelect(setAcquisitionChannel, t.label)}>
            <div className="topic-icon">{t.icon}</div><div className="topic-label">{t.label}</div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderStep48 = () => (
    <div className="onboarding-step text-center centered-content">
      <h2 className="interstitial-title mt-4 mb-4">Finalizing your<br/>profile...</h2>
      <div className="loading-circle-wrapper my-4">
        <div className="loading-circle"><div className="inner-circle">99%</div></div>
      </div>
    </div>
  );

  const renderStep49 = () => (
    <div className="onboarding-step text-center">
      <h2 className="interstitial-title mt-4 mb-2">Save your progress</h2>
      <p className="interstitial-subtitle mb-4">Enter your email to secure your discount and custom plan.</p>
      
      <div className="input-field-container mt-2 mb-4 w-100">
        <span className="input-icon"><Mail /></span>
        <input 
          type="email" 
          className="dark-input w-100" 
          placeholder="Email address" 
          value={userEmail} 
          onChange={(e) => setUserEmail(e.target.value)} 
        />
      </div>
      
      <div className="checkbox-container text-left w-100 mb-4 pl-2">
        <label className="text-sm color-gray" style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
          <input 
            type="checkbox" 
            checked={wantsUpdates} 
            onChange={(e) => setWantsUpdates(e.target.checked)} 
          />
          Send me tips and special offers
        </label>
      </div>
    </div>
  );

  const handleSpin = () => {
    if (isSpinning) return;
    setIsSpinning(true);
    setWheelDeg(wheelDeg + 360 * 5 + 45); 
    setTimeout(() => {
      setIsSpinning(false);
      setStep(55);
    }, 3500);
  };

  const renderStep50 = () => (
    <div className="onboarding-step text-center centered-content">
      <h2 className="interstitial-title mt-4 mb-4">Creating your personal<br/>Study Plan</h2>
      <div className="progress-container w-100 mb-2"><div className="progress-fill" style={{ width: '65%', transition: 'width 2.5s ease-in-out' }}></div></div>
      <p className="text-sm color-gray mb-4">Analyzing your language goals</p>
      
      <div className="review-box text-center mt-4">
        <h3 className="mb-2 text-white">Excellent</h3>
        <div className="stars" style={{fontSize: '1.5rem'}}>★★★★★</div>
        <div className="text-sm color-gray mt-2 flex-center gap-1"><span className="star" style={{color: '#FFD700'}}><Star size={14} fill="#FFD700" /></span> Trustpilot</div>
      </div>
    </div>
  );

  const renderStep51 = () => (
    <div className="onboarding-step text-center centered-content">
      <div className="check-circle my-4"><Check size={64} /></div>
      <h2 className="interstitial-title mb-4">Your personal<br/>Study Plan is ready</h2>
      
      <div className="review-box text-center mt-4 w-100">
        <h3 className="mb-2 text-white">Excellent</h3>
        <div className="stars" style={{fontSize: '1.5rem'}}>★★★★★</div>
        <div className="text-sm color-gray mt-2 flex-center gap-1"><span className="star" style={{color: '#FFD700'}}><Star size={14} fill="#FFD700" /></span> Trustpilot</div>
      </div>
      <button className="continue-blue-btn mt-auto" onClick={nextStep}>Continue</button>
    </div>
  );

  const renderStep52 = () => (
    <div className="onboarding-step text-center centered-content">
      <div className="tutor-compare-grid w-100 mt-4 mb-4">
        <div className="tutor-compare-card">
          <img src="https://i.pravatar.cc/150?img=68" alt="Human" />
          <h4>Human<br/>Tutor</h4>
          <div className="cost red">$$$</div>
        </div>
        <div className="tutor-compare-card active">
          <img src="https://i.pravatar.cc/150?img=47" alt="AI" />
          <h4>VocaAI<br/>Tutor</h4>
          <div className="cost green">$</div>
        </div>
      </div>
      <h2 className="interstitial-title mt-4">Our tutor is <span className="text-green">25x cheaper</span></h2>
      <p className="interstitial-subtitle">than a human tutor ($20+/hour)</p>
      <button className="continue-blue-btn mt-auto" onClick={nextStep}>Continue</button>
    </div>
  );

  const renderStep53 = () => (
    <div className="onboarding-step text-center centered-content">
      <div className="briefcase-icon mb-4"><Briefcase size={80} color="var(--brand-indigo)" /><span className="receipt-tag">RECEIPT</span></div>
      <h2 className="interstitial-title mb-2">Your company can pay for VocaAI!</h2>
      <p className="interstitial-subtitle">48% of employers reimburse language learning tools — maybe yours will too!</p>
      <button className="continue-blue-btn mt-auto" onClick={nextStep}>Continue</button>
    </div>
  );

  const renderStep54 = () => (
    <div className="onboarding-step text-center centered-content">
      <h2 className="interstitial-title">Spin and save big.</h2>
      <p className="interstitial-subtitle mb-4">Up to <span className="text-green font-bold">50% off</span> your plan</p>
      
      <div className="wheel-wrapper my-4">
        <div className="wheel-pointer"><MapPin size={40} color="#EF4444" fill="#EF4444" /></div>
        <div className="wheel" style={{ transform: `rotate(${wheelDeg}deg)` }}>
          <div className="wheel-slice" style={{ transform: 'rotate(0deg)' }}><span>50%</span></div>
          <div className="wheel-slice dark" style={{ transform: 'rotate(60deg)' }}><span>10%</span></div>
          <div className="wheel-slice" style={{ transform: 'rotate(120deg)' }}><span>30%</span></div>
          <div className="wheel-slice dark" style={{ transform: 'rotate(180deg)' }}><span>5%</span></div>
          <div className="wheel-slice" style={{ transform: 'rotate(240deg)' }}><span>50%</span></div>
          <div className="wheel-slice dark" style={{ transform: 'rotate(300deg)' }}><span>No prize</span></div>
        </div>
      </div>

      <button className="white-btn w-100 mt-auto" onClick={handleSpin} disabled={isSpinning}>
        {isSpinning ? 'Spinning...' : 'Spin to Save'}
      </button>
    </div>
  );

  const renderStep55 = () => (
    <div className="onboarding-step text-center centered-content confetti-bg">
      <h2 className="interstitial-title">Congrats!<br/>You've unlocked</h2>
      <div className="discount-pill my-2">50% discount</div>
      
      <div className="wheel-wrapper my-4" style={{opacity: 0.8}}>
        <div className="wheel-pointer"><MapPin size={40} color="#EF4444" fill="#EF4444" /></div>
        <div className="wheel" style={{ transform: `rotate(${wheelDeg}deg)` }}>
          <div className="wheel-slice"><span>50%</span></div>
          <div className="wheel-slice dark" style={{ transform: 'rotate(60deg)' }}><span>10%</span></div>
          <div className="wheel-slice" style={{ transform: 'rotate(120deg)' }}><span>30%</span></div>
          <div className="wheel-slice dark" style={{ transform: 'rotate(180deg)' }}><span>5%</span></div>
          <div className="wheel-slice" style={{ transform: 'rotate(240deg)' }}><span>50%</span></div>
          <div className="wheel-slice dark" style={{ transform: 'rotate(300deg)' }}><span>No prize</span></div>
        </div>
      </div>

      <button className="white-btn w-100 mt-auto" onClick={nextStep} style={{boxShadow: '0 0 30px rgba(255,255,255,0.4)'}}>
        Claim discount
      </button>
    </div>
  );

  const renderStep56 = () => (
    <div className="onboarding-step">
      <div className="offer-header text-center mb-4">
        <p className="text-sm color-gray mb-1">
          {userName ? `${userName}, to` : 'To'} help you {motivation ? motivation.toLowerCase() : 'reach your goals'} we're giving you
        </p>
        <h1 className="hero-title text-green">50% off</h1>
      </div>

      <div className="before-after-card mb-4">
        <div className="ba-col">
          <div className="ba-title">Now</div>
          <p>Worried.<br/>Frozen.<br/>Hesitant.</p>
        </div>
        <div className="ba-col active">
          <div className="ba-title text-blue">After 3 months</div>
          <p>Confident.<br/>Calm.<br/>Precise.</p>
        </div>
      </div>

      <h3 className="text-white mb-2">Get unlimited access to your personal plan</h3>
      <div className="discount-applied-banner mb-4">
        <span className="text-sm flex-center gap-1"><Check size={16} /> 50% discount applied</span>
        <span className="timer flex-center gap-1"><Clock size={16} /> 14:59</span>
      </div>

      <div className="pricing-options">
        {[
          { id: '1-week', title: '1-week trial', price: '$9.99', sub: '$29.99/mo after', total: '$9.99 today' },
          { id: '12-week', title: '12-week plan', price: '$34.41', sub: '$11.47/mo, billed every 12 weeks', total: '$34.41 today', popular: true },
          { id: '52-week', title: '52-week plan', price: '$83.88', sub: '$6.99/mo, billed yearly', total: '$83.88 today' }
        ].map((plan) => (
          <div key={plan.id} className={`pricing-card ${selectedPlan === plan.id ? 'active' : ''}`} onClick={() => setSelectedPlan(plan.id)}>
            {plan.popular && <div className="popular-tag">MOST POPULAR</div>}
            <div className="pricing-content">
              <div className="pricing-radio"></div>
              <div>
                <div className="pricing-title">{plan.title}</div>
                <div className="pricing-sub">{plan.sub}</div>
              </div>
              <div className="pricing-price">
                <span className="price-big">{plan.price}</span><br/>
                <span className="price-small">/week</span>
              </div>
            </div>
          </div>
        ))}
      </div>
      <p className="text-center text-sm color-gray my-3">30-day money-back guarantee</p>
    </div>
  );

  const renderStep57 = () => (
    <div className="onboarding-step">
      <h2 className="interstitial-title mb-4">Complete your checkout</h2>
      
      <div className="checkout-summary mb-4">
        <div className="summary-row"><span>{selectedPlan} plan</span><span className="strike">$59.99</span></div>
        <div className="summary-row total"><span>Total today</span><span>{selectedPlan === '1-week' ? '$9.99' : selectedPlan === '12-week' ? '$34.41' : '$83.88'}</span></div>
      </div>

      <div className="checkout-form">
        <div className="form-header">
          <span className="text-white font-bold">Credit Card</span>
          <div className="cc-icons flex-center gap-1"><CreditCard size={20} /> <CreditCard size={20} /> <CreditCard size={20} /></div>
        </div>
        <input type="text" className="cc-input" placeholder="Card Number" />
        <div className="cc-row">
          <input type="text" className="cc-input" placeholder="MM / YY" />
          <input type="text" className="cc-input" placeholder="CVC" />
        </div>
        <button className="continue-blue-btn mt-3 flex-center gap-2" onClick={() => navigate('/dashboard')}><CreditCard size={20} /> Pay</button>
      </div>
      
      <p className="text-sm color-gray text-center mt-4 flex-center gap-1"><Lock size={14} /> Secured by Stripe • 256-bit encryption</p>
    </div>
  );
  
  const showContinueFooter = [1, 2, 4, 6, 8, 11, 12, 13, 14, 17, 18, 19, 20, 21, 27, 28, 29, 30, 32, 33, 35, 36, 37, 38, 39, 40, 42, 43, 44, 49, 56].includes(step);
  const hideHeader = [23, 24, 25, 31, 34, 45, 46, 48, 49, 50, 51, 54, 55].includes(step);
  const isButtonEnabled = canContinue();

  return (
    <div className="onboarding-page">
      <div className="onboarding-container">
        
        {!hideHeader && (
          <div className="onboarding-header">
            {step !== 32 ? (
               <button className="back-arrow" onClick={prevStep}>❮</button>
            ) : <div className="spacer"></div>}
            
            <div className="progress-container">
              <div className="progress-fill" style={{ width: `${(step / totalSteps) * 100}%` }}></div>
            </div>
            
            {step === 21 ? (
              <button className="skip-btn" onClick={() => setStep(23)}>Skip</button>
            ) : <div className="spacer"></div>}
          </div>
        )}

        <div className="onboarding-content">
          {step === 1 && renderStep1()}
          {step === 2 && renderStep2()}
          {step === 3 && renderStep3()}
          {step === 4 && renderStep4()}
          {step === 5 && renderStep5()}
          {step === 6 && renderStep6()}
          {step === 7 && renderStep7()}
          {step === 8 && renderStep8()}
          {step === 9 && renderStep9()}
          {step === 10 && renderStep10()}
          {step === 11 && renderStep11()}
          {step === 12 && renderStep12()}
          {step === 13 && renderStep13()}
          {step === 14 && renderStep14()}
          {step === 15 && renderStep15()}
          {step === 16 && renderStep16()}
          {step === 17 && renderStep17()}
          {step === 18 && renderStep18()}
          {step === 19 && renderStep19()}
          {step === 20 && renderStep20()}
          {step === 21 && renderStep21()}
          {step === 22 && renderStep22()}
          {step === 23 && renderStep23()}
          {step === 24 && renderStep24()}
          {step === 25 && renderStep25()}
          {step === 26 && renderStep26()}
          {step === 27 && renderStep27()}
          {step === 28 && renderStep28()}
          {step === 29 && renderStep29()}
          {step === 30 && renderStep30()}
          {step === 31 && renderStep31()}
          {step === 32 && renderStep32()}
          {step === 33 && renderStep33()}
          {step === 34 && renderStep34()}
          {step === 35 && renderStep35()}
          {step === 36 && renderStep36()}
          {step === 37 && renderStep37()}
          {step === 38 && renderStep38()}
          {step === 39 && renderStep39()}
          {step === 40 && renderStep40()}
          {step === 41 && renderStep41()}
          {step === 42 && renderStep42()}
          {step === 43 && renderStep43()}
          {step === 44 && renderStep44()}
          {step === 45 && renderStep45()}
          {step === 46 && renderStep46()}
          {step === 47 && renderStep47()}
          {step === 48 && renderStep48()} 
          {step === 49 && renderStep49()} 
          {step === 50 && renderStep50()}
          {step === 51 && renderStep51()}
          {step === 52 && renderStep52()}
          {step === 53 && renderStep53()}
          {step === 54 && renderStep54()}
          {step === 55 && renderStep55()}
          {step === 56 && renderStep56()}
          {step === 57 && renderStep57()}
        </div>

        {showContinueFooter && (
          <div className="onboarding-footer">
            <button 
              className={`continue-blue-btn ${isButtonEnabled ? 'pulse-ready' : ''}`} 
              onClick={nextStep}
              disabled={!isButtonEnabled}
            >
              Continue
            </button>
          </div>
        )}

      </div>
    </div>
  );
};

export default OnboardingPage;